import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import { ChevronDown, ChevronRight, Search, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useSidebar } from "@/components/ui/use-sidebar"
import { cn } from "@/lib/utils"

const DEFAULT_VISIBLE_COUNT = 5

type SidebarNavSectionProps = {
  allItemsShown?: boolean
  children: React.ReactNode
  className?: string
  defaultExpanded?: boolean
  forceExpanded?: boolean
  headerActions?: React.ReactNode
  icon: React.ReactNode
  isSearchActive?: boolean
  listMaxHeight?: string
  searchPlaceholder?: string
  searchTerm?: string
  stickyShowLess?: boolean
  storageKey: string
  title: string
  /** Stable data-testid for the expand/collapse chevron button (e2e hook). */
  toggleTestId?: string
  totalCount?: number
  visibleCount?: number
  onCollapseChange?: (isOpen: boolean) => void
  onHeaderClick?: () => void
  onSearchChange?: (value: string) => void
  onSearchClose?: () => void
  onSeeAllClick?: () => void
  onShowLessClick?: () => void
}

function SidebarNavSectionInner({
  allItemsShown = false,
  children,
  className,
  defaultExpanded = true,
  forceExpanded = false,
  headerActions,
  icon,
  isSearchActive = false,
  listMaxHeight,
  searchPlaceholder,
  searchTerm = "",
  stickyShowLess = false,
  storageKey,
  title,
  toggleTestId,
  totalCount = 0,
  visibleCount = DEFAULT_VISIBLE_COUNT,
  onCollapseChange,
  onHeaderClick,
  onSearchChange,
  onSearchClose,
  onSeeAllClick,
  onShowLessClick,
}: SidebarNavSectionProps) {
  const { t } = useTranslation()
  const { state: sidebarState, isMobile } = useSidebar()
  const isCollapsedSidebar = sidebarState === "collapsed" && !isMobile
  const searchInputRef = useRef<HTMLInputElement>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const [localSearchValue, setLocalSearchValue] = useState(searchTerm)

  const onCollapseChangeRef = useRef(onCollapseChange)
  onCollapseChangeRef.current = onCollapseChange

  useEffect(() => {
    setLocalSearchValue(searchTerm)
  }, [searchTerm])

  useEffect(() => {
    if (isSearchActive) {
      setTimeout(() => searchInputRef.current?.focus(), 0)
    }
  }, [isSearchActive])

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  const handleSearchInputChange = useCallback(
    (value: string) => {
      setLocalSearchValue(value)
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
      debounceRef.current = setTimeout(() => {
        onSearchChange?.(value)
      }, 150)
    },
    [onSearchChange]
  )

  const handleSearchClose = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    setLocalSearchValue("")
    onSearchChange?.("")
    onSearchClose?.()
  }, [onSearchChange, onSearchClose])

  useEffect(() => {
    if (!isSearchActive) return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        handleSearchClose()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isSearchActive, handleSearchClose])

  const [isOpen, setIsOpenInternal] = useState(() => {
    if (typeof window === "undefined") return defaultExpanded
    const stored = localStorage.getItem(storageKey)
    return stored !== null ? stored === "true" : defaultExpanded
  })

  const [showAllItems, setShowAllItems] = useState(false)

  const setIsOpen = useCallback(
    (open: boolean) => {
      setIsOpenInternal(open)
      localStorage.setItem(storageKey, String(open))

      if (open) {
        onCollapseChangeRef.current?.(open)
      } else {
        setTimeout(() => {
          onCollapseChangeRef.current?.(open)
        }, 150)
      }
    },
    [storageKey]
  )

  useEffect(() => {
    if (forceExpanded && !isOpen) {
      setIsOpen(true)
    }
  }, [forceExpanded, isOpen, setIsOpen])

  useEffect(() => {
    if (!isOpen) {
      setShowAllItems(false)
    }
  }, [isOpen])

  const hasMoreItems = totalCount > visibleCount
  const isShowingAll = showAllItems || allItemsShown
  const shouldShowSeeAll = hasMoreItems && !isShowingAll
  const shouldShowShowLess = hasMoreItems && isShowingAll

  const handleSeeAllClick = useCallback(() => {
    if (onSeeAllClick) {
      onSeeAllClick()
    } else {
      setShowAllItems(true)
    }
  }, [onSeeAllClick])

  const handleShowLessClick = useCallback(() => {
    if (onShowLessClick) {
      onShowLessClick()
    } else {
      setShowAllItems(false)
    }
  }, [onShowLessClick])

  const useStickyButton = stickyShowLess && isShowingAll

  const useNonStickyOverflow = !useStickyButton && isShowingAll && !stickyShowLess
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [isScrollable, setIsScrollable] = useState(false)
  const [isScrolledFromTop, setIsScrolledFromTop] = useState(false)
  const isScrolledFromTopRef = useRef(false)
  const stickyScrollContainerRef = useRef<HTMLDivElement>(null)
  const [stickyMaxHeight, setStickyMaxHeight] = useState<string | undefined>(undefined)
  let resolvedScrollRef: React.RefObject<HTMLDivElement | null> | null = null
  if (useStickyButton) {
    resolvedScrollRef = stickyScrollContainerRef
  } else if (useNonStickyOverflow) {
    resolvedScrollRef = scrollContainerRef
  }

  useLayoutEffect(() => {
    if (!useNonStickyOverflow) {
      setIsScrollable(false)
      return
    }

    const checkScrollable = () => {
      const element = scrollContainerRef.current
      if (element) {
        setIsScrollable(element.scrollHeight > element.clientHeight)
      }
    }

    checkScrollable()
    const resizeObserver = new ResizeObserver(checkScrollable)
    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [useNonStickyOverflow, children])

  useLayoutEffect(() => {
    if (!useStickyButton) {
      setStickyMaxHeight(undefined)
      return
    }

    const computeHeight = () => {
      const element = stickyScrollContainerRef.current
      if (!element) return

      const rect = element.getBoundingClientRect()
      const footerBuffer = 96 // leave room for gradients/buttons at the bottom
      const availableHeight = window.innerHeight - rect.top - footerBuffer
      const safeHeight = Math.max(320, availableHeight)
      setStickyMaxHeight(`${safeHeight}px`)
    }

    computeHeight()
    window.addEventListener("resize", computeHeight)
    return () => window.removeEventListener("resize", computeHeight)
  }, [useStickyButton, children])

  const shouldTrackTopFade = useStickyButton || (useNonStickyOverflow && isScrollable)

  useLayoutEffect(() => {
    if (!shouldTrackTopFade) {
      isScrolledFromTopRef.current = false
      setIsScrolledFromTop(false)
      return
    }

    const scrollEl = useStickyButton ? stickyScrollContainerRef.current : scrollContainerRef.current
    if (!scrollEl) {
      isScrolledFromTopRef.current = false
      setIsScrolledFromTop(false)
      return
    }

    const updateScrolledFromTop = () => {
      const scrolled = scrollEl.scrollTop > 0
      if (scrolled === isScrolledFromTopRef.current) return
      isScrolledFromTopRef.current = scrolled
      setIsScrolledFromTop(scrolled)
    }

    updateScrolledFromTop()
    scrollEl.addEventListener("scroll", updateScrolledFromTop, { passive: true })

    const resizeObserver = new ResizeObserver(updateScrolledFromTop)
    resizeObserver.observe(scrollEl)

    return () => {
      scrollEl.removeEventListener("scroll", updateScrolledFromTop)
      resizeObserver.disconnect()
    }
  }, [shouldTrackTopFade, useStickyButton, children, stickyMaxHeight])

  const sectionContent = (
    <div className={cn("relative flex flex-col gap-1 py-1", useStickyButton && "min-h-0 flex-1")}>
      {useStickyButton && isScrolledFromTop && (
        <div className="from-sidebar pointer-events-none absolute top-0 right-0 left-0 z-10 h-6 bg-gradient-to-b to-transparent" />
      )}
      {useNonStickyOverflow && isScrollable && isScrolledFromTop && (
        <div className="from-sidebar pointer-events-none absolute top-0 right-0 -left-6 z-10 h-4 bg-gradient-to-b to-transparent" />
      )}
      <div
        ref={resolvedScrollRef ?? undefined}
        style={useStickyButton ? { maxHeight: stickyMaxHeight } : undefined}
        className={cn(
          useNonStickyOverflow &&
            cn(
              "-ml-6 overflow-y-auto overscroll-contain pl-6 [-webkit-overflow-scrolling:touch]",
              listMaxHeight ?? "max-h-52"
            ),
          useNonStickyOverflow && isScrollable && "pb-14",
          useStickyButton &&
            "-ml-6 min-h-0 flex-1 overflow-y-auto overscroll-contain pb-12 pl-6 [-webkit-overflow-scrolling:touch]"
        )}
      >
        {children}
      </div>
      {shouldShowSeeAll && (
        <Button
          className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground mt-1 h-7 w-full justify-start rounded-md px-3 text-xs"
          variant="ghost"
          onClick={handleSeeAllClick}
        >
          {t("sidebar.see_all", { defaultValue: "See all" })} ({totalCount})
        </Button>
      )}
      {shouldShowShowLess && !useStickyButton && isScrollable && (
        <div className="pointer-events-none absolute right-0 bottom-0 -left-6 z-10 flex flex-col overflow-hidden">
          <div className="from-sidebar h-6 bg-gradient-to-t to-transparent" />
          <div className="bg-sidebar pb-1">
            <Button
              className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground pointer-events-auto h-7 w-full justify-start rounded-md pr-3 pl-6 text-xs"
              variant="ghost"
              onClick={handleShowLessClick}
            >
              {t("sidebar.show_less", { defaultValue: "Show less" })}
            </Button>
          </div>
        </div>
      )}
      {shouldShowShowLess && !useStickyButton && !isScrollable && (
        <Button
          className="text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground mt-1 h-7 w-full justify-start px-3 text-xs"
          variant="ghost"
          onClick={handleShowLessClick}
        >
          {t("sidebar.show_less", { defaultValue: "Show less" })}
        </Button>
      )}
      {useStickyButton && (
        <div className="pointer-events-none absolute right-0 bottom-0 left-0 z-10 flex flex-col">
          <div className="from-sidebar h-8 bg-gradient-to-t to-transparent" />
          <div className="bg-sidebar pb-1">
            <Button
              className="text-muted-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground pointer-events-auto h-7 w-full justify-start px-3 text-xs"
              variant="ghost"
              onClick={handleShowLessClick}
            >
              {t("sidebar.show_less", { defaultValue: "Show less" })}
            </Button>
          </div>
        </div>
      )}
    </div>
  )

  if (isCollapsedSidebar) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "group/nav-section focus-visible:ring-ring flex size-10 items-center justify-center rounded-md transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
              "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
              className
            )}
          >
            <span className="flex size-6 items-center justify-center [&>svg]:size-6">{icon}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-2" side="right" sideOffset={8}>
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-semibold">{title}</span>
            {headerActions}
          </div>
          <div className="max-h-[300px] overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
            {sectionContent}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  return (
    <Collapsible className={cn("flex flex-col", className)} open={isOpen} onOpenChange={setIsOpen}>
      {isSearchActive ? (
        <div
          ref={searchContainerRef}
          className="flex h-9 w-full items-center gap-2 rounded-lg px-2"
        >
          <Search className="text-muted-foreground size-4 shrink-0" />
          <Input
            ref={searchInputRef}
            className="placeholder:text-muted-foreground flex-1 border-none bg-transparent px-0 text-sm shadow-none focus-visible:ring-0"
            value={localSearchValue}
            placeholder={
              searchPlaceholder ?? t("sidebar.search_placeholder", { defaultValue: "Search..." })
            }
            onChange={(event) => handleSearchInputChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                handleSearchClose()
              }
            }}
          />
          <Button
            aria-label={t("common.close", { defaultValue: "Close" })}
            className="hover:bg-foreground/15 dark:hover:bg-foreground/10 size-6 shrink-0 rounded-full p-0"
            size="icon"
            variant="ghost"
            onClick={handleSearchClose}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : (
        <div
          className={cn(
            "group/nav-section flex h-9 w-full items-center justify-between rounded-lg px-2 transition-colors duration-150"
          )}
        >
          <div className="flex flex-1 items-center">
            <CollapsibleTrigger asChild>
              <button
                aria-label={isOpen ? "Collapse section" : "Expand section"}
                className="hover:bg-foreground/20 focus-visible:ring-ring -ml-1 flex size-6 items-center justify-center rounded-md transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                data-testid={toggleTestId}
                type="button"
                onClick={(event) => event.stopPropagation()}
              >
                <span className="text-sidebar-foreground flex size-4 items-center justify-center group-hover/nav-section:hidden">
                  {icon}
                </span>
                <ChevronDown
                  className={cn(
                    "text-sidebar-foreground hidden size-4 transition-transform duration-150 group-hover/nav-section:block",
                    isOpen && "rotate-180"
                  )}
                />
              </button>
            </CollapsibleTrigger>
            <div
              role={onHeaderClick ? "button" : undefined}
              tabIndex={onHeaderClick ? 0 : undefined}
              className={cn(
                "group/header-link flex flex-1 items-center gap-1 py-1 pr-2 pl-1",
                onHeaderClick && "hover:text-sidebar-accent-foreground cursor-pointer rounded-md"
              )}
              onClick={onHeaderClick}
              onKeyDown={(event) => {
                if (onHeaderClick && (event.key === "Enter" || event.key === " ")) {
                  event.preventDefault()
                  onHeaderClick()
                }
              }}
            >
              <span className="text-sidebar-foreground text-sm font-semibold">{title}</span>
              {onHeaderClick && (
                <ChevronRight className="text-sidebar-foreground size-3.5 shrink-0 opacity-0 transition-opacity duration-150 group-hover/header-link:opacity-70" />
              )}
            </div>
          </div>
          {headerActions && (
            <div
              className={cn(
                "flex items-center gap-1 transition-opacity duration-150",
                "opacity-100 md:opacity-0 md:group-hover/nav-section:opacity-100"
              )}
            >
              {headerActions}
            </div>
          )}
        </div>
      )}

      <CollapsibleContent
        className={cn("flex min-h-0 flex-col overflow-hidden", useStickyButton ? "flex-1" : "")}
      >
        <div
          className={cn(
            "border-sidebar-border ml-4 border-l pl-2",
            useStickyButton && "flex min-h-0 flex-1 flex-col"
          )}
        >
          {sectionContent}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

export const SidebarNavSection = memo(SidebarNavSectionInner)
